import { useMemo, useRef, useState } from "react";
import {
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import type {
    IProviderData,
    ISimpleIntegration,
} from "Pages/Integration/EditIntegrations2/types";
import styles from "Pages/Integration/EditIntegrations2/EditIntegrations2.module.css";

/**
 * Payload for the existing POST /api/integrations/edit endpoint. The server
 * (Controllers/Admin/EditIntegrations.cs:237) builds the actual Integration
 * rows from these fields — the requested integration_group is intentionally
 * ignored by that endpoint (it sets "New Group"), so the parent component
 * follows up with a PUT to write the target group.
 */
export interface IAddPayload {
    process: string;
    short_name: string;
    description: string;
    direction: string;
    from: string;
    to: string;
    args: string[];
}

function normalizeDirection(raw: string | null | undefined): string {
    if (!raw) return "";
    const t = raw.trim();
    return t.startsWith("Direction.") ? t.slice("Direction.".length) : t;
}

function keyOf(p: string, args: string[] | null | undefined): string {
    return args && args.length > 0 ? `${p}/${args.join("/")}` : p;
}

interface IAvailable {
    key: string;
    process: string;
    description: string;
    direction: string;
    from: string;
    to: string;
    short_name: string;
    args: string[];
    providerName: string;
}

interface IProps {
    providerData: IProviderData[];
    activeIntegrations: IIntegration[];
    existingGroups: string[];
    companyId: string;
    onClose: () => void;
    /** Receives the simple POST-shape rows plus the chosen target group.
     * Parent does the two-step POST-then-PUT to write group. */
    onSubmit: (payload: IAddPayload[], targetGroup: string) => Promise<void>;
}

export default function AddIntegrationModal2({
    providerData,
    activeIntegrations,
    existingGroups,
    companyId,
    onClose,
    onSubmit,
}: IProps) {
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [targetGroup, setTargetGroup] = useState("");
    const [newGroupName, setNewGroupName] = useState("");
    const [saving, setSaving] = useState(false);
    const filterInputRef = useRef<HTMLInputElement>(null);

    /** Used as modal onOpened handler. autoFocus prop on input did not work
    probably due to component mounting / modal animation. */
    const autoFocusFilterInput = () => {
        if (filterInputRef.current) {
            filterInputRef.current.focus();
        }
    };

    /** Available = provider metadata minus rows already active for the company,
     * deduped by full process+args key. */
    const available: IAvailable[] = useMemo(() => {
        const existing = new Set(activeIntegrations.map((r) => r.process.toLowerCase()));
        const seen = new Set<string>();
        const out: IAvailable[] = [];
        for (const p of providerData ?? []) {
            for (const integ of p.integrations ?? []) {
                if (!integ.process) continue;
                if (existing.has(integ.process.toLowerCase())) continue;
                const key = keyOf(integ.process, integ.args);
                if (seen.has(key.toLowerCase())) continue;
                seen.add(key.toLowerCase());
                out.push({
                    key,
                    process: integ.process,
                    description: integ.description ?? "",
                    direction: integ.direction ?? "",
                    from: integ.from ?? "",
                    to: integ.to ?? "",
                    short_name: integ.short_name ?? "",
                    args: integ.args ?? [],
                    providerName: p.name ?? "",
                });
            }
        }
        return out.sort((a, b) => a.process.localeCompare(b.process));
    }, [providerData, activeIntegrations]);

    /** Depth of every provider in the LIVE inheritance chain, resolved only from
     * the base_class links the provider service reports (IProviderData.base_class,
     * built server-side by EditIntegrations.cs:122 GetRelevantClientProviders) —
     * never from a folder or file name, which does not reliably name the class.
     * ProviderBase — or any provider whose base_class is not itself in the
     * metadata — is depth 0; each subclass is one deeper. Cycle-guarded so a
     * self- or mutually-referencing base_class cannot spin. */
    const providerDepths = useMemo(() => {
        const baseOf = new Map<string, string>();
        for (const p of providerData ?? []) {
            if (p.name) baseOf.set(p.name, p.base_class ?? "");
        }
        const depths = new Map<string, number>();
        for (const name of baseOf.keys()) {
            let depth = 0;
            let cur = name;
            const seen = new Set<string>([name]);
            for (;;) {
                const base = baseOf.get(cur);
                if (!base || !baseOf.has(base) || seen.has(base)) break;
                seen.add(base);
                cur = base;
                depth++;
            }
            depths.set(name, depth);
        }
        return depths;
    }, [providerData]);

    /** One group per declaring provider class, sorted by rank DESCENDING: the
     * most-derived provider's own integrations first, ProviderBase's inherited
     * ones last.
     *
     * rank = inheritance depth, with two floors below every real depth:
     *  -1  ProviderBase — pinned last however the chain resolves, so a provider
     *      with a broken base_class link (depth 0, but not the root class) can
     *      never displace it.
     *  -2  a provider whose depth did not resolve at all (name absent from the
     *      metadata). Sorts after ProviderBase but still renders its own group —
     *      a visible home, never a silent omission.
     *
     * Same-depth siblings sort alphabetically by class name: a plain, stable
     * tiebreak. A single client's chain normally has one provider per depth, so
     * it only bites if the metadata ever reports two. */
    const byProvider = useMemo(() => {
        const map = new Map<string, IAvailable[]>();
        for (const a of available) {
            if (!map.has(a.providerName)) map.set(a.providerName, []);
            map.get(a.providerName)!.push(a);
        }
        const rankOf = (name: string): number => {
            const depth = providerDepths.get(name);
            if (depth === undefined) return -2;
            if (name === "ProviderBase") return -1;
            return depth;
        };
        return Array.from(map.entries()).sort(([aName], [bName]) => {
            const aRank = rankOf(aName);
            const bRank = rankOf(bName);
            if (aRank !== bRank) return bRank - aRank;
            return aName.localeCompare(bName);
        });
    }, [available, providerDepths]);

    const providerNames = byProvider.map(([n]) => n).filter(Boolean);
    const titleSuffix =
        providerNames.length === 0
            ? ""
            : ` (${providerNames.join(" / ")})`;

    const fq = filter.trim().toLowerCase();
    const matches = (a: IAvailable) => {
        if (!fq) return true;
        const haystack = `${a.process} ${a.description} ${a.short_name}`.toLowerCase();
        return haystack.includes(fq);
    };
    const visibleAvailable = available.filter(matches);

    const toggleOne = (key: string) => {
        setSelected((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const toggleAllVisible = () => {
        const allOn = visibleAvailable.every((a) => selected.has(a.key));
        setSelected((cur) => {
            const next = new Set(cur);
            for (const a of visibleAvailable) {
                if (allOn) next.delete(a.key);
                else next.add(a.key);
            }
            return next;
        });
    };

    const resolvedGroup =
        targetGroup === "__new__" ? newGroupName.trim() : targetGroup.trim();
    const canSubmit = selected.size > 0 && resolvedGroup.length > 0;

    const hint = (() => {
        if (selected.size === 0 && !resolvedGroup) return "Pick rows and a group.";
        if (selected.size === 0) return "Pick at least one row.";
        if (!resolvedGroup)
            return targetGroup === "__new__" ? "Type a name for the new group." : "Pick a group.";
        return "";
    })();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        const byKey = new Map(available.map((a) => [a.key, a]));
        const payload: IAddPayload[] = [];
        for (const key of selected) {
            const a = byKey.get(key);
            if (!a) continue;
            payload.push({
                process: a.process,
                short_name: a.short_name || "",
                // description must be non-null per server validation
                description: a.description ?? "",
                direction: normalizeDirection(a.direction),
                from: a.from,
                to: a.to,
                args: a.args ?? [],
            });
        }
        await onSubmit(payload, resolvedGroup);
        setSaving(false);
    };

    return (
        <Modal isOpen toggle={onClose} size="lg" onOpened={autoFocusFilterInput}>
            <ModalHeader toggle={onClose}>Add Integrations{titleSuffix}</ModalHeader>
            <form onSubmit={handleSubmit}>
                <ModalBody className={styles.addModalBody}>
                    <Input
                        type="search"
                        bsSize="sm"
                        placeholder="Filter by process name or description…"
                        innerRef={filterInputRef}
                        value={filter}
                        onChange={(e) => setFilter(e.currentTarget.value)}
                        className={styles.addFilter}
                    />

                    <div className={styles.addCounts}>
                        <span>
                            {fq
                                ? `${visibleAvailable.length} of ${available.length}`
                                : available.length}{" "}
                            available
                        </span>
                        <button
                            type="button"
                            className={styles.linkBtn}
                            onClick={toggleAllVisible}
                        >
                            {visibleAvailable.every((a) => selected.has(a.key)) &&
                            visibleAvailable.length > 0
                                ? "Unselect All"
                                : "Select All"}
                        </button>
                    </div>

                    <div className={styles.addList}>
                        {byProvider.map(([providerName, rows]) => {
                            const visibleRows = rows.filter(matches);
                            if (visibleRows.length === 0) return null;
                            return (
                                <div key={providerName}>
                                    <div className={styles.addProviderHeader}>
                                        {providerName || "(provider unknown)"}
                                    </div>
                                    {visibleRows.map((a) => {
                                        const argSuffix =
                                            a.args.length > 0
                                                ? `(${a.args.join(", ")})`
                                                : "";
                                        const label = a.description || a.short_name || "";
                                        return (
                                            <label key={a.key} className={styles.addRow}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(a.key)}
                                                    onChange={() => toggleOne(a.key)}
                                                />
                                                <span className={styles.addProcessCell}>
                                                    <code className={styles.addProcess}>
                                                        {a.process}
                                                    </code>
                                                    {argSuffix && (
                                                        <code className={styles.addArgs}>
                                                            {argSuffix}
                                                        </code>
                                                    )}
                                                </span>
                                                {label && (
                                                    <span className={styles.addDesc}>{label}</span>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </ModalBody>
                <ModalFooter className={styles.addFooter}>
                    <span className={styles.addSelectedCount}>{selected.size} selected</span>
                    <span className={styles.addGroup}>
                        Group <span className={styles.addRequired}>*</span>:
                        <Input
                            type="select"
                            bsSize="sm"
                            value={targetGroup}
                            onChange={(e) => setTargetGroup(e.currentTarget.value)}
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value="">— pick a group —</option>
                            {existingGroups.map((g) => (
                                <option key={g} value={g}>
                                    {g}
                                </option>
                            ))}
                            <option value="__new__">— new group —</option>
                        </Input>
                        {targetGroup === "__new__" && (
                            <Input
                                type="text"
                                bsSize="sm"
                                placeholder="New group name"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.currentTarget.value)}
                                style={{ width: "12rem", display: "inline-block" }}
                            />
                        )}
                    </span>
                    <span className={styles.addHint}>{hint}</span>
                    <Button type="button" color="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" color="primary" disabled={!canSubmit || saving}>
                        {saving ? "Adding…" : "Add Selected"}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
}
